var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import classNames from 'clsx';
import { FormContext } from '../form';
import { FormContainerFieldContext } from '../form-container-field';
import Icon from '../icon';
import { formDataLayerGA4 } from '../../utilities/data-layer';
import { generateValidationMessage, registerComponentHelper } from '../../utilities/form-helpers';
import { isDarkColor, isValidHexCode } from './utils/colors';
import { FormInputColorColor, FormInputColorColorPreview, FormInputColorContainer, FormInputColorDescription, FormInputColorInput, FormInputColorLabel, FromInputColorPrice, FromInputColorText, } from './styles/form-input-color.styled';
/**
 * The Form Input component can be used to render an input radio button with validation logic
 */
class FormInputColor extends Component {
    constructor() {
        super(...arguments);
        this.componentIsMounted = false;
        /**
         * A set of default validation messages which are used
         */
        this.textValidationMessages = {
            customError: '',
            valueMissing: 'Je hebt nog geen keuze gemaakt.',
            valid: '',
        };
        this.state = {
            valid: undefined,
        };
        this.onChange = (element) => {
            const { handlerState } = this.props;
            const analytics = true;
            if (handlerState)
                handlerState({ value: element.value });
            if (element.validity.customError) {
                element.setCustomValidity('');
            }
            if (element.validity.valid) {
                this.validityHide();
            }
            this.validityCheck(element).then(() => {
                this.validitySet(element);
                this.validityShow(element, analytics);
            });
        };
        this.onBlur = (element) => {
            this.validityCheck(element).then(() => {
                this.validitySet(element);
                this.validityShow(element);
            });
        };
        this.onValidation = () => {
            const { executeSubmit, name, checked, value } = this.props;
            const analytics = !this.element.validity.valid;
            return this.validityCheck(this.element).then(() => {
                this.validitySet(this.element);
                this.validityShow(this.element, analytics);
                executeSubmit(checked && name ? { [name]: value } : {});
            });
        };
        this.validityCheck = (element) => {
            const { handlerValidation } = this.props;
            return new Promise((resolve) => {
                if (element.validity.valid && element.value && handlerValidation) {
                    handlerValidation(element.value)
                        .catch((reason) => {
                        element.setCustomValidity(reason);
                    })
                        .then(() => {
                        resolve();
                    });
                }
                else {
                    resolve();
                }
            });
        };
        this.validitySet = (element) => {
            if (this.componentIsMounted)
                this.setState({ valid: element.validity.valid });
        };
        this.validityShow = (element, analytics) => {
            const { componentName, name, handlerValidationMessage } = this.props;
            const { text, type } = generateValidationMessage(element, this.textValidationMessages);
            if (analytics) {
                formDataLayerGA4({
                    event: 'user_interaction',
                    type: 'form',
                    component: componentName,
                    field: name ? `form-input-${name}` : 'form-input-color',
                    action: 'change',
                    actionValue: element.validity.valid ? 'valid' : `invalid-${type}-${text}`,
                });
            }
            if (!element.validity.valid && handlerValidationMessage) {
                handlerValidationMessage(text);
            }
        };
        this.validityHide = () => {
            const { handlerValidationMessage } = this.props;
            if (handlerValidationMessage)
                handlerValidationMessage('');
        };
        this.componentDidMount = () => {
            this.componentIsMounted = true;
            const { reference, textValidationMessages, registerComponent, checked, handlerState } = this.props;
            const { valid } = this.state;
            registerComponentHelper(registerComponent, {
                reference,
                validation: this.onValidation,
            });
            Object.assign(this.textValidationMessages, textValidationMessages);
            if (checked) {
                this.validityCheck(this.element).then(() => {
                    this.validitySet(this.element);
                    this.validityShow(this.element);
                });
            }
            else if (handlerState) {
                handlerState({ valid });
            }
        };
        this.componentDidUpdate = (_, prevState) => {
            const { handlerState } = this.props;
            const { valid } = this.state;
            if (valid !== prevState.valid && handlerState) {
                handlerState({ valid });
            }
        };
        // TODO: This lifecycle method is deprecated and should be removed: https://reactjs.org/blog/2018/03/27/update-on-async-rendering.html
        this.UNSAFE_componentWillReceiveProps = (nextProps) => {
            const { valid, checked, required } = this.props;
            if (valid !== nextProps.valid ||
                checked !== nextProps.checked ||
                required !== nextProps.required) {
                this.setState({ valid: nextProps.valid }, () => {
                    if (checked) {
                        if (this.element.validity.customError) {
                            this.element.setCustomValidity('');
                        }
                        if (this.element.validity.valid) {
                            this.validityHide();
                        }
                        void this.validityCheck(this.element).then(() => {
                            this.validitySet(this.element);
                            this.validityShow(this.element);
                        });
                    }
                });
            }
        };
    }
    componentWillUnmount() {
        this.componentIsMounted = false;
    }
    render() {
        const _a = this.props, { reference, label, color, colorImage, priceDescription, textDescription, name, value, checked, required, disabled, gradient, textValidationMessages, valid: validProp, onFocus, onChange, onBlur, class: className, dataTest = 'form-input-color', componentName: _componentName, executeSubmit: _executeSubmit, handlerState: _handlerState, handlerValidation: _handlerValidation, handlerValidationMessage: _handlerValidationMessage, registerComponent: _registerComponent, unRegisterComponent: _unRegisterComponent } = _a, props = __rest(_a, ["reference", "label", "color", "colorImage", "priceDescription", "textDescription", "name", "value", "checked", "required", "disabled", "gradient", "textValidationMessages", "valid", "onFocus", "onChange", "onBlur", "class", "dataTest", "componentName", "executeSubmit", "handlerState", "handlerValidation", "handlerValidationMessage", "registerComponent", "unRegisterComponent"]);
        const { valid } = this.state;
        const formInputColorClassNames = classNames('PONCHO-form-input-color', {
            'PONCHO-form-input-color--valid': valid === true,
            'PONCHO-form-input-color--invalid': valid === false,
        }, className);
        const isGradient = gradient === true;
        const isDisabled = disabled === true;
        const formInputColorColorClassNames = classNames({
            'PONCHO-form-input-color__disabled': isDisabled,
            'PONCHO-form-input-color__color': !isDisabled,
        });
        const formInputColorColorInnerClassNames = classNames({
            'PONCHO-form-input-color__disabled__inner': isDisabled,
            'PONCHO-form-input-color__color__inner': !isDisabled,
        });
        if (!colorImage && (color === undefined || !isValidHexCode(color))) {
            return null;
        }
        return (_jsxs(FormInputColorContainer, { "$isDisabled": isDisabled, "$isInValid": valid === false, htmlFor: reference, id: `${reference || ''}-label`, className: formInputColorClassNames, children: [_jsx(FormInputColorInput, Object.assign({}, props, { ref: (element) => {
                        if (element) {
                            this.element = element;
                        }
                    }, type: "radio", id: reference, name: name, value: value, checked: checked, required: required, disabled: disabled, "aria-disabled": disabled, "aria-invalid": valid === false ? true : undefined, onFocus: (event) => {
                        if (onFocus)
                            onFocus(event);
                    }, onChange: (event) => {
                        this.onChange(event.currentTarget);
                        if (onChange)
                            onChange(event);
                    }, onBlur: (event) => {
                        this.onBlur(event.currentTarget);
                        if (onBlur)
                            onBlur(event);
                    } })), _jsx(FormInputColorColor, { className: formInputColorColorClassNames, children: _jsx(FormInputColorColorPreview, { "$isDisabled": isDisabled, "$isDark": isDarkColor(color), "$useGradient": isGradient, "$image": colorImage, "$color": color, "data-test": isDisabled ? `${dataTest}-disabled-color` : `${dataTest}-displayed-color`, className: formInputColorColorInnerClassNames, children: disabled && (_jsx(Icon, { variant: "lock", size: "md", dataTest: `${dataTest}-disabled-icon`, className: "icon-disabled" })) }) }), _jsxs(FromInputColorText, { className: "PONCHO-form-input-color__text", children: [_jsx(FormInputColorLabel, { "$isDisabled": isDisabled, className: disabled && 'PONCHO-form-input-color__text__disabled', children: label }), priceDescription && !disabled && (_jsx(FromInputColorPrice, { "data-test": `${dataTest}-price-description`, children: priceDescription })), textDescription && (_jsx(FormInputColorDescription, { "data-test": `${dataTest}-text-description`, children: textDescription }))] })] }));
    }
}
FormInputColor.defaultProps = {
    checked: false,
    required: false,
    disabled: false,
    textValidationMessages: {},
    componentName: 'form-input-color',
};
export const FormInputColorWithContext = (props) => (_jsx(FormContext.Consumer, { children: ({ registerComponent, unRegisterComponent, executeSubmit }) => (_jsx(FormContainerFieldContext.Consumer, { children: ({ handlerValidationMessage }) => (_jsx(FormInputColor, Object.assign({}, props, { registerComponent: registerComponent, unRegisterComponent: unRegisterComponent, executeSubmit: executeSubmit, handlerValidationMessage: handlerValidationMessage }))) })) }));
export default FormInputColorWithContext;
